import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { UserState } from '@/types';
import { getUser, loginUser, logoutUser, User } from '@/tmp';
import { rethrowIfNotClientException } from '@/utilities/throwableUtils';

function clearLocalStorage() {
  localStorage.clear();
}

export function useUserState(
  initialState: UserState,
  params: URLSearchParams
): [UserState, Dispatch<SetStateAction<UserState>>] {
  const [value, setValue] = useState<UserState>(initialState);

  useEffect(() => {
    function logoutAtExpiryDate(expiryTimeMicros: number) {
      const expiryTimeMillis = expiryTimeMicros / 1000;
      const nowTimeMillis = Date.now();
      const leftTimeMillis = expiryTimeMillis - nowTimeMillis;

      if (leftTimeMillis <= 0) {
        setValue('token_failure');
      } else {
        const maxTimeoutMillis = 15 * 60 * 1000;
        const checkTimeoutMillis = Math.min(leftTimeMillis, maxTimeoutMillis);
        setTimeout(() => {
          logoutAtExpiryDate(expiryTimeMicros);
        }, checkTimeoutMillis);
      }
    }

    async function init() {
      const token = params.get('t');
      const passwordReset = params.get('passwordreset');
      const user = params.get('user');
      const hash = params.get('hash');

      if (token) {
        try {
          // ha sokszor reload-ol akkor az mindig uj login -> too many sessions
          // megnezhetnenk, hogy van-e session-je, de akkor ellenorizni kellene, hogy
          // ehhez a tokenhez tartozik-e, egyszerubb kileptetni
          try {
            await logoutUser();
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (_e) {
            console.debug('logout failed');
          }
          clearLocalStorage();

          const user = await loginUser({
            username: '_token_',
            password: token,
            rememberMe: false,
            isBiometricLogin: false,
          });
          setValue(user);
          if (user.isTokenUser) {
            logoutAtExpiryDate(user.expiry);
          }
          return;
        } catch (error) {
          setValue('token_failure');
          rethrowIfNotClientException(error);
          return;
        }
      }

      // nincs erteke, ezert '' az erteke :(
      if (passwordReset === '') {
        setValue('password_reset');
        return;
      }

      if (hash && user) {
        setValue('change_password');
        return;
      }

      try {
        const response = await getUser();
        if (response.status === 200) {
          const userAndMessage = response.data;

          const user = {
            id: userAndMessage.id,
            name: userAndMessage.name,
            isAdmin: userAndMessage.isAdmin,
            isTokenUser: userAndMessage.isTokenUser,
            expiry: userAndMessage.expiry,
            extraFeatures: userAndMessage.extraFeatures ?? [],
            email: userAndMessage.email,
            hasTachograph: userAndMessage.hasTachograph,
          } satisfies User;
          setValue(user);
          if (user.isTokenUser) {
            logoutAtExpiryDate(user.expiry);
          }
        } else {
          setValue(null);
        }
      } catch (error) {
        setValue(null);
        rethrowIfNotClientException(error);
      }
    }
    void init();
  }, []);

  async function onChange(newState: UserState | ((newState: UserState) => UserState)) {
    if (newState instanceof Function) {
      return; // FIXME
    }
    if (newState === undefined) {
      console.log('not permitted');
      return;
    }
    setValue(newState);
    if (newState === null) {
      await logoutUser();
    }
  }

  return [value, onChange];
}
